/*
 * <collection-tabs> — tabbed collection product panels.
 *
 * - role="tab" buttons switch role="tabpanel" panels ([hidden] on all but one).
 * - Every [data-scroll-track] (the pill row and each product row) is a single
 *   horizontal strip; its sibling [data-scroll-prev]/[data-scroll-next] chevrons
 *   scroll it and show only when it overflows. Chevrons are hidden on mobile
 *   via CSS — the rows stay native swipe scrollers there.
 * Progressive enhancement: with no JS, the first panel shows and rows scroll.
 */
(function () {
  function wireScroller(track) {
    var scope = track.closest('.collection-tabs__nav, .collection-tabs__viewport') || track.parentNode;
    var prev = scope.querySelector('[data-scroll-prev]');
    var next = scope.querySelector('[data-scroll-next]');
    if (!prev || !next) return;

    var step = function () { return Math.max(160, track.clientWidth * 0.8); };

    var update = function () {
      var max = track.scrollWidth - track.clientWidth - 1;
      var overflowing = max > 0;
      var x = track.scrollLeft;
      prev.hidden = !overflowing || x <= 0;
      next.hidden = !overflowing || x >= max;
    };

    prev.addEventListener('click', function () {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    update();
    requestAnimationFrame(update);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', update, { once: true });
    }

    track._ctUpdateArrows = update;
  }

  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[role="tabpanel"]'));

      if (this.tabs.length > 1) {
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
        });
      }

      this.querySelectorAll('[data-scroll-track]').forEach(wireScroller);
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        var active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        var panel = this.panels[i];
        if (panel) panel.hidden = !active;
      });

      var panel = this.panels[index];
      if (panel) {
        var track = panel.querySelector('[data-scroll-track]');
        if (track && track._ctUpdateArrows) requestAnimationFrame(track._ctUpdateArrows);
      }

      var activeTab = this.tabs[index];
      if (activeTab && activeTab.scrollIntoView) {
        activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    onKeydown(e, i) {
      var last = this.tabs.length - 1;
      var target = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') target = i === last ? 0 : i + 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') target = i === 0 ? last : i - 1;
      else if (e.key === 'Home') target = 0;
      else if (e.key === 'End') target = last;
      if (target === null) return;
      e.preventDefault();
      this.select(target);
      this.tabs[target].focus();
    }
  }

  if (!customElements.get('collection-tabs')) {
    customElements.define('collection-tabs', CollectionTabs);
  }
})();
